import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CODE_LENGTH,
  formatPence,
  isValidCodeShape,
  normalizeCode,
  type CodeCheck,
  type PublicEvent,
} from '@quiz/shared';
import { api } from '../lib/api.js';
import { loadCodes, saveCode, forgetCode } from '../lib/session.js';
import { Screen, Card, Button, Banner, Logo, SupportCta } from '../components/ui.js';

/**
 * What a player sees when they open the site.
 *
 * Everything on tonight, what each one costs, and whether this phone is already
 * paid up for it. When a host starts one, the card turns into a call to action
 * loud enough to notice across a pub.
 */
export function HomeScreen({
  onPlay,
  activeEventId,
  callSign,
}: {
  onPlay: (event: PublicEvent) => void;
  /** An event this phone is already in, so we can offer to go back to it. */
  activeEventId: string | null;
  callSign: string | null;
}) {
  const [events, setEvents] = useState<PublicEvent[] | null>(null);
  const [codes, setCodes] = useState(loadCodes);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api<{ events: PublicEvent[] }>('/api/events');
      setEvents(res.events);
      setError(null);
    } catch {
      setError('Cannot reach the quiz right now.');
    }
  }, []);

  useEffect(() => {
    void load();
    // Frequent enough that "the host has started" feels immediate in a room
    // where somebody has just said "right, phones out".
    const id = setInterval(() => void load(), 4000);
    const onVisible = () => document.visibilityState === 'visible' && void load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  // Only the event they can actually walk into is worth vibrating a pocket for.
  const live = useMemo(() => events?.filter((e) => e.hasStarted && e.joinable) ?? [], [events]);
  useStartedAlert(live);

  if (error && !events) {
    return (
      <Screen>
        <div className="flex flex-1 items-center">
          <Banner kind="error">{error}</Banner>
        </div>
      </Screen>
    );
  }

  return (
    <Screen>
      <header className="mb-6 mt-2 text-center">
        <Logo className="mb-4 h-20" />
        <h1 className="text-3xl font-bold">Quiz night</h1>
        <p className="mt-1 text-muted">
          {callSign ? `Welcome back, ${callSign}.` : 'Pick a quiz to play.'}
        </p>
      </header>

      {events === null && <p className="text-center text-muted">Looking…</p>}

      {events?.length === 0 && (
        <Card className="text-center">
          <p className="text-lg font-semibold">Nothing on right now</p>
          <p className="mt-2 text-muted">
            Check back nearer the time, or ask the staff when the next one is.
          </p>
        </Card>
      )}

      <ul className="space-y-3">
        {events?.map((e) => (
          <EventCard
            key={e.id}
            event={e}
            savedCode={codes[e.id] ?? null}
            isActive={e.id === activeEventId}
            onPlay={() => onPlay(e)}
            onSaveCode={(code) => {
              saveCode(e.id, code);
              setCodes(loadCodes());
            }}
            onForgetCode={() => {
              forgetCode(e.id);
              setCodes(loadCodes());
            }}
          />
        ))}
      </ul>

      {events && events.length > 0 && (
        <p className="mt-6 text-center text-xs text-muted">
          Keep this page open. When the host starts, it will tell you.
        </p>
      )}

      {/* Below the quizzes, not above them: somebody who opened this to join a
          quiz should reach the join button first. */}
      <SupportCta className="mt-6" />
    </Screen>
  );
}

function EventCard({
  event,
  savedCode,
  isActive,
  onPlay,
  onSaveCode,
  onForgetCode,
}: {
  event: PublicEvent;
  savedCode: string | null;
  isActive: boolean;
  onPlay: () => void;
  onSaveCode: (code: string) => void;
  onForgetCode: () => void;
}) {
  const [entering, setEntering] = useState(false);

  const started = event.hasStarted;
  /**
   * The next one up. Shown so a player can see there is a nine o'clock as well
   * as a seven o'clock, with the button dead rather than removed -- the card
   * keeps its shape, so the page does not jump when it becomes their turn.
   */
  const locked = !event.joinable;

  return (
    <li
      className={`rounded-2xl p-5 transition ${
        locked ? 'bg-ink-2/50 opacity-60' : started ? 'bg-go/15 ring-2 ring-go' : 'bg-ink-2'
      }`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-xl font-semibold">{event.name}</h2>
        {locked && (
          <span className="shrink-0 rounded-full bg-ink-3 px-2.5 py-1 text-xs font-semibold text-muted">
            NEXT UP
          </span>
        )}
        {!locked && started && (
          <span className="shrink-0 animate-pulse rounded-full bg-go px-2.5 py-1 text-xs font-bold text-ink">
            LIVE
          </span>
        )}
      </div>

      <p className="mt-1 text-sm text-muted">
        <When iso={event.scheduledFor} isPractice={event.isPractice} />
        {event.playerCount > 0 && ` · ${event.playerCount} playing`}
      </p>

      <div className="mt-3">
        <Entry event={event} savedCode={savedCode} />
      </div>

      {locked ? (
        <div className="mt-4 space-y-2">
          <Button variant="ghost" disabled>
            Not open yet
          </Button>
          <p className="text-center text-xs text-muted">
            This one opens when the quiz before it has finished.
          </p>
        </div>
      ) : started ? (
        <div className="mt-4">
          <Button variant="go" onClick={onPlay}>
            {isActive ? 'Back to the quiz' : 'Join now'}
          </Button>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          <Button variant={event.isPractice ? 'primary' : 'ghost'} onClick={onPlay}>
            {/*
              "Buy a ticket" only when there is actually a ticket to buy: a free
              shared-code night has nothing to sell, and somebody whose phone
              already holds a code has bought one -- telling either of them to
              buy is how you get a player queueing at the door for nothing.
            */}
            {isActive
              ? 'Back to the quiz'
              : event.isPractice
                ? 'Play now'
                : event.isFree || savedCode
                  ? 'Get ready'
                  : 'Buy a ticket'}
          </Button>

          {event.requiresCode && !savedCode && !entering && (
            <Button variant="ghost" onClick={() => setEntering(true)}>
              I already have a code
            </Button>
          )}
          {event.requiresCode && savedCode && (
            <Button variant="ghost" onClick={onForgetCode}>
              Not my code
            </Button>
          )}
        </div>
      )}

      {entering && (
        <SaveCode
          eventId={event.id}
          onSaved={(code) => {
            onSaveCode(code);
            setEntering(false);
          }}
          onCancel={() => setEntering(false)}
        />
      )}
    </li>
  );
}

/** What it costs and whether this phone is already sorted. */
function Entry({ event, savedCode }: { event: PublicEvent; savedCode: string | null }) {
  if (event.isPractice) {
    return (
      <p className="text-sm text-go">Free practice &middot; no code, no charge, play as often as you like</p>
    );
  }
  if (savedCode) {
    return (
      <p className="text-sm text-go">
        You&rsquo;re in &mdash; code <span className="font-mono font-bold">{savedCode}</span>
      </p>
    );
  }
  if (event.joinMode === 'CODE') {
    return <p className="text-sm text-muted">Free &middot; use the code on your table</p>;
  }
  return (
    <p className="text-sm text-flag">
      {formatPence(event.entryFeePence)} &middot; pay at the door for a code
    </p>
  );
}

function When({ iso, isPractice }: { iso: string | null; isPractice: boolean }) {
  if (!iso) return <>{isPractice ? 'Any time' : 'No date set'}</>;

  const when = new Date(iso);
  const today = new Date();
  const sameDay = when.toDateString() === today.toDateString();
  const time = when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  if (sameDay) return <span className="text-chalk">Tonight at {time}</span>;
  return <>{when.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} at {time}</>;
}

function SaveCode({
  eventId,
  onSaved,
  onCancel,
}: {
  eventId: string;
  onSaved: (code: string) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [check, setCheck] = useState<CodeCheck | null>(null);
  const [busy, setBusy] = useState(false);

  async function verify() {
    setBusy(true);
    try {
      // Checked, not spent: the code is still good when the quiz starts.
      const res = await api<CodeCheck>(`/api/events/${eventId}/code?code=${code}`);
      setCheck(res);
      if (res.usable) onSaved(code);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 rounded-xl bg-ink p-3">
      <input
        value={code}
        onChange={(e) => {
          setCode(normalizeCode(e.target.value).slice(0, CODE_LENGTH));
          setCheck(null);
        }}
        autoFocus
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        placeholder="ABC234"
        className="w-full bg-transparent text-center font-mono text-3xl tracking-[0.25em] outline-none placeholder:text-ink-3"
      />
      {check && !check.usable && <p className="mt-2 text-center text-sm text-stop">{check.message}</p>}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button onClick={onCancel} className="min-h-11 rounded-lg bg-ink-3 text-sm">
          Cancel
        </button>
        <button
          onClick={() => void verify()}
          disabled={!isValidCodeShape(code) || busy}
          className="min-h-11 rounded-lg bg-sky text-sm font-semibold text-ink disabled:opacity-40"
        >
          {busy ? 'Checking…' : 'Save it'}
        </button>
      </div>
      <p className="mt-2 text-center text-xs text-muted">
        Saving it does not use it up. Your phone just remembers you&rsquo;re paid.
      </p>
    </div>
  );
}

/**
 * The alert.
 *
 * A pub is loud and phones are face-down on the table, so this leans on the two
 * things that get through a pocket -- a vibration and a flashing tab title --
 * rather than on Notification permission, which iOS Safari will not grant to a
 * page that has not been installed.
 *
 * Note the refs. An earlier version kept "already alerted" in state, so setting
 * it re-ran the effect, whose own cleanup put the title straight back: the alert
 * fired and undid itself within a frame. Nothing here may live in a dependency
 * that this effect also writes.
 */
function useStartedAlert(live: PublicEvent[]) {
  const alerted = useRef<Set<string>>(new Set());
  const flipping = useRef<number | null>(null);
  const original = useRef<string>(document.title);

  const stopFlashing = useCallback(() => {
    if (flipping.current !== null) {
      clearInterval(flipping.current);
      flipping.current = null;
    }
    document.title = original.current;
  }, []);

  useEffect(() => {
    const fresh = live.filter((e) => !alerted.current.has(e.id));
    if (fresh.length === 0) return;
    for (const e of fresh) alerted.current.add(e.id);

    try {
      navigator.vibrate?.([200, 100, 200]);
    } catch {
      /* unsupported, and not important enough to notice */
    }

    const shout = '▶ The quiz has started!';
    stopFlashing();
    document.title = shout;
    flipping.current = window.setInterval(() => {
      document.title = document.title === shout ? original.current : shout;
    }, 1200);

    // Long enough to catch somebody coming back to a pocketed phone, short
    // enough that it is not still blinking when they are three questions in.
    const stop = window.setTimeout(stopFlashing, 60_000);
    return () => window.clearTimeout(stop);
  }, [live, stopFlashing]);

  // Whatever else happens, do not leave the title flashing behind.
  useEffect(() => stopFlashing, [stopFlashing]);
}
